/**
 * Import/Export Utilities
 * Provides functionality to import and export data as CSV/Excel
 */

import * as XLSX from 'xlsx';
import Papa from 'papaparse';

/**
 * Export data to Excel file
 * @param {Array} data - Array of objects to export
 * @param {string} filename - The Excel filename
 * @param {string} sheetName - The sheet name (default: "Data")
 */
export const exportToExcel = (data, filename, sheetName = 'Data') => {
  try {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Adjust column widths
    const columnWidths = [];
    if (data.length > 0) {
      Object.keys(data[0]).forEach((key) => {
        columnWidths.push({ wch: Math.max(key.length, 15) });
      });
      worksheet['!cols'] = columnWidths;
    }

    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw error;
  }
};

/**
 * Export data to CSV file
 * @param {Array} data - Array of objects to export
 * @param {string} filename - The CSV filename
 */
export const exportToCSV = (data, filename) => {
  try {
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    throw error;
  }
};

/**
 * Import data from CSV file
 * @param {File} file - The CSV file to import
 * @param {Function} onComplete - Callback function with parsed data
 * @param {Function} onError - Callback function for errors
 */
export const importFromCSV = (file, onComplete, onError) => {
  try {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        onComplete(results.data);
      },
      error: (error) => {
        onError(error);
      },
    });
  } catch (error) {
    console.error('Error importing CSV:', error);
    onError(error);
  }
};

/**
 * Import data from Excel file
 * @param {File} file - The Excel file to import
 * @param {Function} onComplete - Callback function with parsed data
 * @param {Function} onError - Callback function for errors
 */
export const importFromExcel = (file, onComplete, onError) => {
  try {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        onComplete(jsonData);
      } catch (error) {
        onError(error);
      }
    };
    reader.readAsBinaryString(file);
  } catch (error) {
    console.error('Error importing Excel:', error);
    onError(error);
  }
};

/**
 * Format data for export by renaming keys
 * @param {Array} data - Data to format
 * @param {Object} keyMap - Object mapping old keys to new keys
 * @returns {Array} Formatted data
 */
export const formatDataForExport = (data, keyMap) => {
  return data.map((item) => {
    const newItem = {};
    Object.entries(keyMap).forEach(([oldKey, newKey]) => {
      if (item.hasOwnProperty(oldKey)) {
        newItem[newKey] = item[oldKey];
      }
    });
    return newItem;
  });
};

/**
 * Prepare file input handler
 * @param {Function} importHandler - Handler for file selection
 * @returns {Function} Click handler for file input
 */
export const createImportHandler = (importHandler) => {
  return () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        importHandler(file);
      }
    };
    input.click();
  };
};
